<template>
    <div :class="wide ? 'pr-shell pr-shell--query' : 'pr-shell'">
        <div class="pr-grid" :class="{ 'pr-grid--wide': wide }">
            <div class="pr-label">
                <slot name="label">
                    <form-label class="pr-label-text">{{ label }}</form-label>
                </slot>
            </div>
            <div class="pr-control">
                <slot />
            </div>
            <div class="pr-status">
                <!-- Default status is the shared indicator, driven by the two
                     booleans the caller passes; the slot stays as an escape
                     hatch for a row that needs something else there. -->
                <slot name="status">
                    <property-override-indicator
                        :resettable="resettable"
                        :modified="modified"
                        :inherited="inherited"
                        @reset="$emit('reset')" />
                </slot>
            </div>
        </div>
    </div>
</template>

<script>
// The single layout primitive for a property row: label | control | status.
//
// A CSS grid instead of the older `w-2/5` + flex-1 pairs, because the sidebar
// is user-resizable down to ~260px: the control column is minmax(0, 1fr) so it
// really shrinks, the status column is a constant width so nothing can ever
// overlap it, and the label column wraps instead of running over the control.
//
// `wide` is for controls that need more horizontal room than a narrow sidebar
// can give them (border side's six toggles, any segmented group): the control
// moves to its own line below label+status when the row is narrower than
// WIDE_INLINE_MIN, and sits inline again when there is room. That decision is a
// container query on the row itself, so it follows the sidebar width with no JS
// and no per-property hardcoding.
import PropertyOverrideIndicator from './PropertyOverrideIndicator.vue';

export default {
    name: 'PropertyRow',
    components: { PropertyOverrideIndicator },

    props: {
        label: { type: String, default: '' },
        wide: { type: Boolean, default: false },
        // Override status, forwarded to PropertyOverrideIndicator: the row owns
        // the status column so no specialized editor has to know about icons or
        // tooltips (see PropertyOverrideIndicator.vue for the prop semantics).
        resettable: { type: Boolean, default: null },
        modified: { type: Boolean, default: false },
        inherited: { type: Boolean, default: false },
    },

    emits: ['reset'],
};
</script>

<style scoped>
/* Only wide rows become query containers: container-type makes the element a
   containing block for absolutely/fixed-positioned descendants, which would
   otherwise move popovers (e.g. the color picker's panel) around. */
.pr-shell--query {
    container-type: inline-size;
}

.pr-grid {
    display: grid;
    align-items: center;
    column-gap: 0.25rem;
    row-gap: 0.125rem;
    grid-template-columns: minmax(4.5rem, 40%) minmax(0, 1fr) 1.125rem;
    grid-template-areas: 'label control status';
}

/* Narrow default for wide controls: control on its own full-width line. */
.pr-grid--wide {
    grid-template-columns: minmax(0, 1fr) 1.125rem;
    grid-template-areas:
        'label status'
        'control control';
}

@container (min-width: 300px) {
    .pr-grid--wide {
        grid-template-columns: minmax(4.5rem, 40%) minmax(0, 1fr) 1.125rem;
        grid-template-areas: 'label control status';
    }
}

.pr-label {
    grid-area: label;
    min-width: 0;
}

/* Labels wrap rather than overflow their column — no clipping, no overlap. */
.pr-label-text {
    overflow-wrap: anywhere;
}

.pr-control {
    grid-area: control;
    min-width: 0;
}

.pr-status {
    grid-area: status;
    display: flex;
    justify-content: flex-end;
}

/* Controls handed in through the default slot must be able to shrink too. */
.pr-control :deep(input),
.pr-control :deep(select),
.pr-control :deep(textarea) {
    min-width: 0;
    max-width: 100%;
}
</style>
